'use client'

import { useCallback, useEffect, useRef } from 'react'

import type { View } from './twinContent'
import type { TwinInteraction } from './twinTypes'

// Thresholds and payloads: see docs/unveiling.md#twin-engagement-tracking.
const ENGAGED_AFTER_INTERACTIONS = 3
const ENGAGED_AFTER_SECONDS = 30
const ACTIVE_WINDOW_MS = 5000
// OrbitControls reports every wheel tick as its own start/end, so a zoom this soon after the last one is the same burst.
const SAME_ZOOM_MS = 1000
const DEPTH: View[] = ['hall', 'rack', 'tray', 'gpu']
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'wheel', 'touchmove', 'keydown'] as const

const push = (data: Record<string, unknown>) => {
  ;(window.dataLayer = window.dataLayer || []).push(data)
}

export function useTwinEngagement(enabled: boolean) {
  const tally = useRef({
    count: 0,
    seconds: 0,
    deepest: 0,
    lastInput: -Infinity,
    lastKind: null as TwinInteraction | null,
    lastZoomAt: -Infinity,
    interacted: false,
    engaged: false,
    completed: false,
  })

  const engage = useCallback((kind: TwinInteraction | 'none') => {
    const t = tally.current
    if (t.engaged) return
    t.engaged = true
    push({
      event: 'twin_engaged',
      twin_interaction: kind,
      twin_seconds: t.seconds,
      twin_level: DEPTH[t.deepest],
    })
  }, [])

  const note = useCallback(
    (kind: TwinInteraction, view: View) => {
      if (!enabled) return
      const t = tally.current
      const now = performance.now()
      t.lastInput = now
      t.lastKind = kind
      t.deepest = Math.max(t.deepest, DEPTH.indexOf(view))

      const sameBurst = kind === 'zoom' && now - t.lastZoomAt < SAME_ZOOM_MS
      if (kind === 'zoom') t.lastZoomAt = now
      if (!sameBurst) t.count += 1
      if (!t.interacted) {
        t.interacted = true
        push({ event: 'twin_interacted', twin_interaction: kind, twin_level: view })
      }
      if (t.count >= ENGAGED_AFTER_INTERACTIONS) engage(kind)

      // Coalesced zooms can leave the count short, so reaching the module engages first.
      if (view === 'gpu' && !t.completed) {
        engage(kind)
        t.completed = true
        push({ event: 'twin_completed', twin_seconds: t.seconds, twin_interactions: t.count })
      }
    },
    [enabled, engage],
  )

  useEffect(() => {
    if (!enabled) return
    const onInput = (e: Event) => {
      if (e.type === 'pointermove' && !(e as PointerEvent).buttons) return
      tally.current.lastInput = performance.now()
    }
    INPUT_EVENTS.forEach((ev) => window.addEventListener(ev, onInput, { passive: true }))

    const tick = window.setInterval(() => {
      const t = tally.current
      if (document.visibilityState !== 'visible') return
      if (performance.now() - t.lastInput > ACTIVE_WINDOW_MS) return
      t.seconds += 1
      if (t.seconds >= ENGAGED_AFTER_SECONDS) engage(t.lastKind ?? 'none')
    }, 1000)

    return () => {
      INPUT_EVENTS.forEach((ev) => window.removeEventListener(ev, onInput))
      window.clearInterval(tick)
    }
  }, [enabled, engage])

  return note
}
